import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plus, Trash2, Copy, Search, Save, FileText, ChevronDown, Check, Sun, Moon, Sunset, CloudSun, Zap, Package, AlertTriangle, ShoppingCart } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DrawerShell from './DrawerShell';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import VoiceDictationButton, { VoiceConfirmModal } from './VoiceDictationButton';
import { findAllergyConflicts } from '../utils/allergyCheck';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIMING_OPTIONS = [
  { code: 'M', label: 'Morning', icon: Sun },
  { code: 'A', label: 'Afternoon', icon: CloudSun },
  { code: 'E', label: 'Evening', icon: Sunset },
  { code: 'N', label: 'Night', icon: Moon },
];

// Frequency → max allowed timing selections
const FREQ_MAX_TIMINGS = { OD: 1, BD: 2, TDS: 3, QID: 4, HS: 1, SOS: 1, STAT: 1, WEEKLY: 1, ALT_DAY: 1 };
// Default timing presets per frequency
const FREQ_DEFAULT_TIMINGS = { OD: ['M'], BD: ['M', 'N'], TDS: ['M', 'A', 'N'], QID: ['M', 'A', 'E', 'N'], HS: ['N'], SOS: [], STAT: [], WEEKLY: ['M'], ALT_DAY: ['M'] };

// Frequencies whose meaning is dictated by semantics (not just dose count) — we
// never auto-derive these from timing length. If the doctor picks one of these,
// it stays picked.
const FREQ_SEMANTIC = new Set(['HS', 'SOS', 'STAT', 'WEEKLY', 'ALT_DAY']);

// Derive frequency from the number of selected timing dots.
// Used to keep the Frequency column in sync when the doctor toggles dots on the
// timing pills — before this sync existed, every medicine rendered as whatever
// the initial default was (BD), even when the doctor selected only 1 dot.
function deriveFrequencyFromTiming(timing, currentFreq) {
  const count = (timing || []).length;
  // If doctor has picked a semantic frequency (HS / SOS / etc.), don't override
  if (FREQ_SEMANTIC.has(currentFreq)) return currentFreq;
  if (count === 1) return 'OD';
  if (count === 2) return 'BD';
  if (count === 3) return 'TDS';
  if (count === 4) return 'QID';
  return currentFreq || '';
}

function parseDurationDays(dur) {
  if (!dur) return 0;
  const s = dur.toLowerCase().replace(/\s+/g, '');
  const num = parseFloat(s) || 0;
  if (s.includes('week') || s.includes('wk') || s.includes('w')) return Math.ceil(num * 7);
  if (s.includes('month') || s.includes('mo')) return Math.ceil(num * 30);
  return Math.ceil(num); // default days
}

function calcQuantity(frequency, duration, timing, doses) {
  // Sum daily doses. When `doses` is supplied, slot dose can be 1 or 0.5;
  // otherwise each entry in `timing` counts as a full dose for backward
  // compatibility.
  let dosesPerDay;
  if (doses && typeof doses === 'object') {
    dosesPerDay = ['M', 'A', 'E', 'N'].reduce((sum, c) => {
      const d = doses[c];
      if (d === 0.5) return sum + 0.5;
      if (d === 1) return sum + 1;
      // If timing list has the slot but doses doesn't specify, treat as 1
      return sum + ((timing || []).includes(c) ? 1 : 0);
    }, 0);
  } else {
    dosesPerDay = (timing && timing.length > 0) ? timing.length : (FREQ_MAX_TIMINGS[frequency] || 1);
  }
  if (!dosesPerDay) dosesPerDay = (FREQ_MAX_TIMINGS[frequency] || 1);
  const days = parseDurationDays(duration);
  if (!days) return '';
  // Round up to whole tablets for inventory (half-tablet days still need
  // whole tablets to dispense), but show the exact count for clarity.
  const total = dosesPerDay * days;
  return Number.isInteger(total) ? String(total) : String(Math.ceil(total * 2) / 2);
}

export function PrescriptionSection({ symptomTags = [] }) {
  const { consultation, saveDraft } = useConsultation();
  const [medicines, setMedicines] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [activeSearch, setActiveSearch] = useState(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  // Race-condition guards for the medicine search dropdowns. Without these,
  // a stale "LE" response can clobber the live "LESURIDE" response when the
  // doctor types fast — leading to obviously irrelevant results.
  const topSearchAbortRef = useRef(null);
  const topSearchReqIdRef = useRef(0);
  const rowSearchAbortRef = useRef(null);
  const rowSearchReqIdRef = useRef(0);
  const [quickAdds, setQuickAdds] = useState([]);
  const [quickAddSource, setQuickAddSource] = useState('');
  const [topSearchQuery, setTopSearchQuery] = useState('');
  const [topSearchResults, setTopSearchResults] = useState([]);
  const [topSearchTimeout, setTopSearchTimeout] = useState(null);
  const [duplicateAlert, setDuplicateAlert] = useState(null);
  // Patient allergies, fetched once on consultation open. Used to auto-flag
  // prescription rows that conflict with a known allergy or drug class.
  const [allergies, setAllergies] = useState([]);
  // Conflicts the doctor has explicitly dismissed for this session, so the
  // banner stays calm after acknowledgement (per allergy+medicine pair).
  const [dismissedConflicts, setDismissedConflicts] = useState(() => new Set());
  // Voice dictation state — `voicePayload` holds the parsed result while
  // the doctor reviews it in the confirmation modal. Two-step capture
  // prevents an autoload from clobbering an in-progress prescription edit.
  const [voicePayload, setVoicePayload] = useState(null);
  const inputRefs = useRef({});
  const templateDropdownRef = useRef(null);
  const topSearchRef = useRef(null);

  // Load common data and templates
  useEffect(() => {
    const loadData = async () => {
      try {
        const [freqRes, intakeRes, tplRes] = await Promise.all([
          axios.get(`${API_URL}/api/common/frequencies`, { withCredentials: true }),
          axios.get(`${API_URL}/api/common/intakes`, { withCredentials: true }),
          axios.get(`${API_URL}/api/templates`, { withCredentials: true })
        ]);
        setFrequencies(freqRes.data);
        setIntakes(intakeRes.data);
        setTemplates(tplRes.data.filter(t => t.content?.prescription?.length > 0));
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, []);

  // Fetch patient allergies once. We fetch from the patient profile
  // because consultation may not always carry them inline. Use native
  // fetch to bypass the axios refresh-retry chain which can swallow this
  // request's promise during boot.
  useEffect(() => {
    const pid = consultation?.patient_id;
    if (!pid) { setAllergies([]); return; }
    let cancelled = false;
    const tenantSlug = window.localStorage?.getItem('tenantSlug') || '';
    fetch(`${API_URL}/api/patients/${pid}/profile`, {
      credentials: 'include',
      headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) setAllergies(data?.patient?.allergies || []); })
      .catch(() => { /* fail silently — allergy check is best-effort */ });
    return () => { cancelled = true; };
  }, [consultation?.patient_id]);

  // Compute live conflicts between current Rx and patient allergies.
  const allergyConflicts = useMemo(
    () => findAllergyConflicts(allergies, medicines),
    [allergies, medicines]
  );
  // Map medicine name → conflict, for fast per-row lookup.
  const conflictByMed = useMemo(() => {
    const m = new Map();
    for (const c of allergyConflicts) {
      const key = (c.medicine || '').toLowerCase();
      if (!m.has(key)) m.set(key, c);
    }
    return m;
  }, [allergyConflicts]);
  // Active (non-dismissed) conflicts — drives the banner visibility.
  const activeConflicts = useMemo(
    () => allergyConflicts.filter(c => !dismissedConflicts.has(`${c.medicine}|${c.allergy}`)),
    [allergyConflicts, dismissedConflicts]
  );

  // Initialize medicines from consultation
  useEffect(() => {
    if (consultation?.sections?.prescription?.items) {
      let items = consultation.sections.prescription.items;
      // One-time self-heal: if a row has a timing count that contradicts its
      // stored frequency (common cause: legacy rows created when everything
      // auto-defaulted to BD then had timing dots customized), re-derive
      // frequency from timing so the print column matches the doctor's intent.
      let healed = false;
      items = items.map(m => {
        if (!m) return m;
        const nextFreq = deriveFrequencyFromTiming(m.timing, m.frequency);
        if (nextFreq && nextFreq !== m.frequency) {
          healed = true;
          return { ...m, frequency: nextFreq };
        }
        return m;
      });
      setMedicines(items);
      if (healed) saveDraft('prescription', items);
      // Enrich any medicines missing stock data
      const unenriched = items.filter((m, i) => m.medicine_name && m.stock == null);
      if (unenriched.length > 0) {
        const names = unenriched.map(m => m.medicine_name);
        axios.post(`${API_URL}/api/common/medicines/enrich`, { names }, { withCredentials: true })
          .then(({ data }) => {
            setMedicines(prev => {
              const updated = prev.map(m => {
                if (m.medicine_name && m.stock == null && data[m.medicine_name]) {
                  const info = data[m.medicine_name];
                  return { ...m, stock: info.stock, composition: info.composition, brand: info.brand, manufacturer: info.manufacturer, subCategory: info.subCategory };
                }
                return m;
              });
              return updated;
            });
          })
          .catch(() => {});
      }
    }
  }, [consultation?.id]);

  // Close template dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setShowTemplateDropdown(false);
        setTemplatePreview(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load quick-add suggestions based on symptoms
  useEffect(() => {
    const loadQuickAdds = async () => {
      try {
        const symptomsStr = consultation?.sections?.chief_complaints?.content || '';
        const { data } = await axios.get(`${API_URL}/api/quick-adds`, {
          params: { symptoms: symptomsStr, section_type: 'prescription' },
          withCredentials: true
        });
        setQuickAdds(data.medicines || []);
        setQuickAddSource(data.source || '');
      } catch (e) { /* ignore */ }
    };
    loadQuickAdds();
  }, [consultation?.sections?.chief_complaints?.content]);

  const addMedicine = useCallback(() => {
    const freq = 'BD';
    const dur = '5 days';
    const timing = FREQ_DEFAULT_TIMINGS[freq];
    const newMedicine = {
      id: Date.now(),
      medicine_name: '',
      dosage: '',
      frequency: freq,
      intake: 'after_food',
      timing,
      duration: dur,
      quantity: calcQuantity(freq, dur, timing)
    };
    setMedicines(prev => {
      const updated = [...prev, newMedicine];
      saveDraft('prescription', updated);
      return updated;
    });
    setTimeout(() => {
      inputRefs.current[`${medicines.length}-name`]?.focus();
    }, 100);
  }, [medicines.length, saveDraft]);

  // Top-level inline search (like investigations)
  const handleTopSearch = (query) => {
    setTopSearchQuery(query);
    if (topSearchTimeout) clearTimeout(topSearchTimeout);
    if (!query || query.length < 2) {
      setTopSearchResults([]);
      if (topSearchAbortRef.current) topSearchAbortRef.current.abort();
      return;
    }
    const timeout = setTimeout(async () => {
      if (topSearchAbortRef.current) topSearchAbortRef.current.abort();
      const controller = new AbortController();
      topSearchAbortRef.current = controller;
      const reqId = ++topSearchReqIdRef.current;
      try {
        // Run the master-catalog search AND the tenant lite-inventory
        // lookup in parallel so the doctor sees their own stock at the
        // top of the dropdown before generic master results.
        const [masterRes, invRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/common/medicines`, {
            params: { search: query }, withCredentials: true, signal: controller.signal,
          }),
          axios.get(`${API_URL}/api/clinic-ops/inventory/autocomplete`, {
            params: { q: query }, withCredentials: true, signal: controller.signal,
          }),
        ]);
        if (reqId !== topSearchReqIdRef.current) return; // stale
        const master = masterRes.status === 'fulfilled' ? (masterRes.value.data || []) : [];
        const inv = invRes.status === 'fulfilled' ? (invRes.value.data || []) : [];
        // Merge: inventory items first (with `from_inventory` flag), then
        // master items not already in the inventory list.
        const invNames = new Set(inv.map(i => (i.name || '').toLowerCase()));
        const merged = [
          ...inv.map(i => ({ ...i, from_inventory: true })),
          ...master.filter(m => !invNames.has((m.name || '').toLowerCase())),
        ];
        setTopSearchResults(merged);
      } catch (e) {
        if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
        if (reqId !== topSearchReqIdRef.current) return;
        console.error('Medicine search error:', e);
        setTopSearchResults([]);
      }
    }, 300);
    setTopSearchTimeout(timeout);
  };

  // Add medicine from top search with out-of-stock handling
  const addFromTopSearch = useCallback((result) => {
    // Duplicate check
    if (medicines.some(m => m.medicine_name && m.medicine_name.toLowerCase() === result.name.toLowerCase())) {
      setDuplicateAlert(result.name);
      setTopSearchQuery('');
      setTopSearchResults([]);
      return;
    }
    const freq = 'BD';
    const dur = '5 days';
    const defaultTimings = FREQ_DEFAULT_TIMINGS[freq];
    const newMedicine = {
      id: Date.now() + Math.random(),
      medicine_name: result.name,
      dosage: '',
      frequency: freq,
      intake: 'after_food',
      timing: defaultTimings,
      duration: dur,
      quantity: calcQuantity(freq, dur, defaultTimings),
      stock: result.stock,
      unit: result.unit || result.subCategory || '',
      composition: result.composition || '',
      brand: result.brand || '',
      manufacturer: result.manufacturer || '',
      subCategory: result.subCategory || '',
    };
    setMedicines(prev => {
      const updated = [...prev, newMedicine];
      saveDraft('prescription', updated);
      return updated;
    });
    setTopSearchQuery('');
    setTopSearchResults([]);

    // Low stock / out-of-stock alert and purchase request
    if (result.stock != null && result.stock <= 10) {
      const isOutOfStock = result.stock <= 0;
      toast.warning(
        <div>
          <p className="font-semibold">{isOutOfStock ? 'Stock not available in Pharmacy' : 'Low stock in Pharmacy'}</p>
          <p className="text-xs mt-1">{result.name}{!isOutOfStock ? ` (${Math.floor(result.stock)} remaining)` : ''}</p>
          <p className="text-xs text-gray-500 mt-1">Purchase notification will be provided for immediate purchase of the item.</p>
        </div>,
        { duration: 5000 }
      );
      // Auto-create purchase request
      axios.post(`${API_URL}/api/purchase-requests`, {
        medicine_name: result.name,
        composition: result.composition || '',
        patient_name: consultation?.patient_name || '',
        patient_id: consultation?.patient_id || '',
        quantity: 1,
      }, { withCredentials: true }).catch(() => {});
    }
    topSearchRef.current?.focus();
  }, [saveDraft, consultation]);

  // Enrich medicines with stock + composition from backend cache
  const enrichMedicines = useCallback(async (meds, startIndex) => {
    const names = meds.map(m => m.medicine_name).filter(Boolean);
    if (names.length === 0) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/common/medicines/enrich`, { names }, { withCredentials: true });
      setMedicines(prev => {
        const updated = [...prev];
        for (let i = 0; i < meds.length; i++) {
          const idx = startIndex + i;
          if (idx >= updated.length) break;
          const info = data[updated[idx].medicine_name];
          if (info) {
            updated[idx] = { ...updated[idx], stock: info.stock, composition: info.composition, brand: info.brand, manufacturer: info.manufacturer, subCategory: info.subCategory };
          }
        }
        saveDraft('prescription', updated);
        return updated;
      });
    } catch (e) { /* silent - enrichment is best-effort */ }
  }, [saveDraft]);

  const addMedicineFromSuggestion = useCallback((med) => {
    const medName = med.name || med.medicine_name || '';
    const freq = med.frequency || 'BD';
    const dur = med.duration || '5 days';
    const defaultTimings = FREQ_DEFAULT_TIMINGS[freq] || ['M', 'N'];
    const newMedicine = {
      id: Date.now() + Math.random(),
      medicine_name: med.name || med.medicine_name || '',
      dosage: med.dosage || '',
      frequency: freq,
      intake: med.intake || 'after_food',
      timing: med.timing || defaultTimings,
      duration: dur,
      quantity: med.quantity || calcQuantity(freq, dur, defaultTimings)
    };
    let added = false;
    let startIdx = -1;
    let nextList = null;
    setMedicines(prev => {
      // Functional dedupe — using prev (latest queued state) so back-to-back
      // synchronous adds (template forEach) don't all re-check a stale
      // closure copy.
      if (medName && prev.some(m => m.medicine_name && m.medicine_name.toLowerCase() === medName.toLowerCase())) {
        return prev;
      }
      added = true;
      startIdx = prev.length;
      nextList = [...prev, newMedicine];
      return nextList;
    });
    if (!added) {
      setDuplicateAlert(medName);
      return;
    }
    if (nextList) saveDraft('prescription', nextList);
    enrichMedicines([newMedicine], startIdx);
  }, [saveDraft, enrichMedicines]);

  // Bulk-add used by the AI panel when applying a saved template. One
  // setState + one saveDraft + one enrichMedicines fan-out — avoids the
  // per-item race entirely.
  const addManyMedicinesFromSuggestion = useCallback((meds) => {
    if (!Array.isArray(meds) || meds.length === 0) return;
    const newMeds = meds.map(med => {
      const freq = med.frequency || 'BD';
      const dur = med.duration || '5 days';
      const defaultTimings = FREQ_DEFAULT_TIMINGS[freq] || ['M', 'N'];
      return {
        id: Date.now() + Math.random(),
        medicine_name: med.name || med.medicine_name || '',
        dosage: med.dosage || '',
        frequency: freq,
        intake: med.intake || 'after_food',
        timing: med.timing || defaultTimings,
        duration: dur,
        quantity: med.quantity || calcQuantity(freq, dur, defaultTimings),
      };
    });
    let nextList = null;
    let firstNewIdx = -1;
    let actuallyAdded = [];
    setMedicines(prev => {
      const existing = new Set(
        prev.map(m => (m.medicine_name || '').trim().toLowerCase()).filter(Boolean)
      );
      actuallyAdded = newMeds.filter(m => {
        const key = (m.medicine_name || '').trim().toLowerCase();
        if (!key || existing.has(key)) return false;
        existing.add(key);
        return true;
      });
      if (actuallyAdded.length === 0) {
        nextList = prev;
        return prev;
      }
      firstNewIdx = prev.length;
      nextList = [...prev, ...actuallyAdded];
      return nextList;
    });
    if (actuallyAdded.length === 0) return;
    if (nextList) saveDraft('prescription', nextList);
    enrichMedicines(actuallyAdded, firstNewIdx);
  }, [saveDraft, enrichMedicines]);

  const updateMedicine = useCallback((index, field, value) => {
    setMedicines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === 'frequency') {
        // Reset timings to defaults for the new frequency
        const defaultTimings = FREQ_DEFAULT_TIMINGS[value] || [];
        updated[index].timing = defaultTimings;
        // Reset half-doses when frequency changes — defaults assume 1 each.
        const newDoses = {};
        defaultTimings.forEach(c => { newDoses[c] = 1; });
        updated[index].timing_doses = newDoses;
        const dur = updated[index].duration;
        const auto = calcQuantity(value, dur, defaultTimings, newDoses);
        if (auto) updated[index].quantity = auto;
      } else if (field === 'duration') {
        const freq = updated[index].frequency;
        const timing = updated[index].timing || [];
        const doses = updated[index].timing_doses;
        const auto = calcQuantity(freq, value, timing, doses);
        if (auto) updated[index].quantity = auto;
      } else if (field === 'timing') {
        // Recalculate quantity when timing changes
        const freq = updated[index].frequency;
        const dur = updated[index].duration;
        const doses = updated[index].timing_doses;
        const auto = calcQuantity(freq, dur, value, doses);
        if (auto) updated[index].quantity = auto;
      }

      saveDraft('prescription', updated);
      return updated;
    });
  }, [saveDraft]);

  const deleteMedicine = useCallback((index) => {
    setMedicines(prev => {
      const updated = prev.filter((_, i) => i !== index);
      saveDraft('prescription', updated);
      return updated;
    });
  }, [saveDraft]);

  const duplicateMedicine = useCallback((index) => {
    setMedicines(prev => {
      const newMedicine = { ...prev[index], id: Date.now() };
      const updated = [...prev];
      updated.splice(index + 1, 0, newMedicine);
      saveDraft('prescription', updated);
      return updated;
    });
  }, [saveDraft]);

  // Voice dictation — normalises Claude's parser output into the shape
  // `addManyMedicinesFromSuggestion` expects. Frequency/intake values from
  // the LLM are converted into our internal slug constants so dropdowns
  // and the quantity calculator behave identically to a manual entry.
  const handleVoiceConfirm = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const normalised = items.map((m) => {
      const f = String(m.frequency || 'BD').toUpperCase().replace(/[^A-Z]/g, '');
      const validFreq = ['OD','BD','TDS','QID','HS','SOS','STAT','WEEKLY','ALT_DAY'];
      const freq = validFreq.includes(f) ? f : 'BD';

      // Map "Before Food" / "After Food" → our slug; default to after_food.
      let intake = 'after_food';
      const intakeRaw = String(m.intake || '').toLowerCase();
      if (intakeRaw.includes('before')) intake = 'before_food';
      else if (intakeRaw.includes('with')) intake = 'with_food';

      // Convert timing string "1-0-1" → ['M','N'] array our UI uses.
      // Doses (the actual numbers) go into `timing_doses`.
      let timingArr;
      let timingDoses;
      const t = (m.timing || '').toString().trim();
      if (/^\d-\d-\d-\d$/.test(t)) {
        const parts = t.split('-').map(Number);
        const slots = ['M', 'A', 'E', 'N'];
        timingArr = slots.filter((_, i) => parts[i] > 0);
        timingDoses = {};
        slots.forEach((s, i) => { if (parts[i] > 0) timingDoses[s] = parts[i]; });
      } else if (/^\d-\d-\d$/.test(t)) {
        const parts = t.split('-').map(Number);
        const slots = ['M', 'A', 'N'];
        timingArr = slots.filter((_, i) => parts[i] > 0);
        timingDoses = {};
        slots.forEach((s, i) => { if (parts[i] > 0) timingDoses[s] = parts[i]; });
      }

      return {
        name: m.medicine_name || '',
        medicine_name: m.medicine_name || '',
        dosage: m.dosage || '',
        frequency: freq,
        intake,
        timing: timingArr,             // undefined → addMany picks frequency defaults
        timing_doses: timingDoses,
        duration: m.duration || '5 days',
        quantity: m.quantity,
        remarks: m.remarks || '',
      };
    }).filter((m) => m.medicine_name);
    if (normalised.length === 0) {
      toast.warning('No valid medicines to add.');
      return;
    }
    addManyMedicinesFromSuggestion(normalised);
  }, [addManyMedicinesFromSuggestion]);

  // Live search medicines from external DB
  const searchMedicines = (query) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    if (!query || query.length < 2) {
      setSearchResults([]);
      if (rowSearchAbortRef.current) rowSearchAbortRef.current.abort();
      return;
    }
    const timeout = setTimeout(async () => {
      if (rowSearchAbortRef.current) rowSearchAbortRef.current.abort();
      const controller = new AbortController();
      rowSearchAbortRef.current = controller;
      const reqId = ++rowSearchReqIdRef.current;
      try {
        const { data } = await axios.get(`${API_URL}/api/common/medicines`, {
          params: { search: query },
          withCredentials: true,
          signal: controller.signal,
        });
        if (reqId !== rowSearchReqIdRef.current) return; // stale
        setSearchResults(data);
      } catch (e) {
        if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
        if (reqId !== rowSearchReqIdRef.current) return;
        console.error('Medicine search error:', e);
        setSearchResults([]);
      }
    }, 300);
    setSearchTimeout(timeout);
  };

  const selectMedicine = (index, medicine) => {
    // Duplicate check (exclude current row)
    if (medicines.some((m, i) => i !== index && m.medicine_name && m.medicine_name.toLowerCase() === medicine.name.toLowerCase())) {
      setDuplicateAlert(medicine.name);
      setSearchResults([]);
      setActiveSearch(null);
      return;
    }
    setMedicines(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        medicine_name: medicine.name,
        stock: medicine.stock,
        unit: medicine.unit || medicine.subCategory || '',
        brand: medicine.brand || '',
        subCategory: medicine.subCategory || '',
        manufacturer: medicine.manufacturer || '',
        composition: medicine.composition || '',
      };
      saveDraft('prescription', updated);
      return updated;
    });
    setSearchResults([]);
    setActiveSearch(null);
    inputRefs.current[`${index}-dosage`]?.focus();

    // Low stock / out-of-stock alert
    if (medicine.stock != null && medicine.stock <= 10) {
      const isOutOfStock = medicine.stock <= 0;
      toast.warning(
        <div>
          <p className="font-semibold">{isOutOfStock ? 'Stock not available in Pharmacy' : 'Low stock in Pharmacy'}</p>
          <p className="text-xs mt-1">{medicine.name}{!isOutOfStock ? ` (${Math.floor(medicine.stock)} remaining)` : ''}</p>
          <p className="text-xs text-gray-500 mt-1">Purchase notification will be provided for immediate purchase of the item.</p>
        </div>,
        { duration: 5000 }
      );
      axios.post(`${API_URL}/api/purchase-requests`, {
        medicine_name: medicine.name,
        composition: medicine.composition || '',
        patient_name: consultation?.patient_name || '',
        patient_id: consultation?.patient_id || '',
        quantity: 1,
      }, { withCredentials: true }).catch(() => {});
    }
  };

  // Toggle timing — the doctor's timing selection drives frequency (not the
  // Cycle the dose at a given slot: off → 1 → ½ → off.
  // We keep the legacy `timing` array (slot codes) for backward compat so
  // existing pharmacy posting / templates / older drafts still work, but
  // the source of truth for the per-slot dose is the new `timing_doses`
  // map ({M:1, A:0, E:0.5, N:1}). On every cycle we re-derive frequency
  // from the count of non-zero slots so the Frequency column stays in
  // sync, and rebuild quantity from the dose total.
  const toggleTiming = (index, code) => {
    const med = medicines[index];
    const doses = { ...(med.timing_doses || {}) };
    // Backfill from legacy timing array for first interaction
    (med.timing || []).forEach(c => { if (doses[c] === undefined) doses[c] = 1; });

    const cur = doses[code] || 0;
    let nextDose;
    if (cur === 0) nextDose = 1;
    else if (cur === 1) nextDose = 0.5;
    else nextDose = 0;
    doses[code] = nextDose;

    // Cap: at most 4 slots active overall
    const activeCount = ['M', 'A', 'E', 'N'].filter(c => (doses[c] || 0) > 0).length;
    if (activeCount > 4) return; // never reachable but keeps invariant explicit

    const nextTiming = ['M', 'A', 'E', 'N'].filter(c => (doses[c] || 0) > 0);
    const nextFreq = deriveFrequencyFromTiming(nextTiming, med.frequency);
    setMedicines(prev => {
      const updated = [...prev];
      const dur = updated[index].duration;
      updated[index] = {
        ...updated[index],
        timing: nextTiming,
        timing_doses: doses,
        frequency: nextFreq,
        quantity: calcQuantity(nextFreq, dur, nextTiming, doses),
      };
      saveDraft('prescription', updated);
      return updated;
    });
  };

  // Load template into prescription
  const loadTemplate = (template) => {
    setTemplatePreview(template);
  };

  const addAllFromTemplate = () => {
    if (!templatePreview) return;
    const newMeds = templatePreview.content.prescription.map(m => ({
      id: Date.now() + Math.random(),
      medicine_name: m.name || '',
      dosage: m.dosage || '',
      frequency: m.frequency || 'BD',
      intake: m.intake || 'after_food',
      timing: m.timing || FREQ_DEFAULT_TIMINGS[m.frequency || 'BD'] || ['M', 'N'],
      duration: m.duration || '5 days',
      quantity: m.quantity || ''
    }));
    setMedicines(prev => {
      const startIdx = prev.length;
      const updated = [...prev, ...newMeds];
      saveDraft('prescription', updated);
      // Enrich all template medicines in one batch call
      enrichMedicines(newMeds, startIdx);
      return updated;
    });
    setTemplatePreview(null);
    setShowTemplateDropdown(false);
  };

  const addSingleFromTemplate = (med) => {
    addMedicineFromSuggestion(med);
  };

  useEffect(() => {
    window.addMedicineFromSuggestion = addMedicineFromSuggestion;
    window.addManyMedicinesFromSuggestion = addManyMedicinesFromSuggestion;
    return () => {
      delete window.addMedicineFromSuggestion;
      delete window.addManyMedicinesFromSuggestion;
    };
  }, [addMedicineFromSuggestion, addManyMedicinesFromSuggestion]);

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || medicines.length === 0) return;
    setSavingTemplate(true);
    try {
      const templateData = {
        name: templateName.trim(),
        category: templateCategory.trim().toLowerCase() || 'custom',
        content: {
          prescription: medicines.map(m => ({
            name: m.medicine_name,
            dosage: m.dosage,
            frequency: m.frequency,
            intake: m.intake,
            timing: m.timing,
            duration: m.duration
          }))
        }
      };
      const { data } = await axios.post(`${API_URL}/api/templates`, templateData, { withCredentials: true });
      setTemplates(prev => [...prev, data]);
      setShowSaveTemplate(false);
      setTemplateName('');
      setTemplateCategory('');
    } catch (error) {
      console.error('Failed to save template:', error);
    }
    setSavingTemplate(false);
  };

  // Reload templates after saving
  const reloadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/templates`, { withCredentials: true });
      setTemplates(data.filter(t => t.content?.prescription?.length > 0));
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="space-y-2 h-full flex flex-col" data-testid="prescription-section">
      {/* Allergy conflict banner — appears the moment a prescribed medicine
          collides with a known patient allergy. The doctor can dismiss
          individual conflicts but they reappear if the offending row stays. */}
      {activeConflicts.length > 0 && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2 flex items-start gap-2 flex-shrink-0" data-testid="allergy-conflict-banner">
          <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {activeConflicts.length} allergy conflict{activeConflicts.length === 1 ? '' : 's'} detected
            </div>
            <ul className="mt-1 space-y-0.5">
              {activeConflicts.map((c, i) => (
                <li key={i} className="text-[12px] text-red-900 flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{c.medicine}</span>
                  <span className="text-red-600">conflicts with allergy</span>
                  <span className="bg-red-100 border border-red-300 rounded px-1.5 py-0 font-bold">{c.allergy}</span>
                  {c.reason === 'class' && c.class && (
                    <span className="text-[10px] uppercase tracking-wider bg-red-200 text-red-700 rounded px-1.5 py-0">
                      {c.class}-class
                    </span>
                  )}
                  <button
                    onClick={() => setDismissedConflicts(s => new Set([...s, `${c.medicine}|${c.allergy}`]))}
                    className="ml-auto text-[10px] text-red-700 hover:text-red-900 underline"
                    data-testid={`dismiss-conflict-${i}`}
                  >
                    Dismiss
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-red-700 mt-1.5 italic">
              Override only if clinically justified. Document the rationale in your notes.
            </p>
          </div>
        </div>
      )}

      {/* Top bar: Grouped actions */}
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        {/* Quick Additions group */}
        <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-0.5 bg-gray-50" ref={templateDropdownRef}>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setShowTemplateDropdown(!showTemplateDropdown); setTemplatePreview(null); }}
              className="gap-1 h-8 text-xs px-2"
              data-testid="template-dropdown-btn"
            >
              <FileText className="h-3.5 w-3.5" />
              Quick Additions
              <ChevronDown className="h-3 w-3" />
            </Button>

            {showTemplateDropdown && (
              <div className="absolute z-50 mt-1 w-[400px] bg-white border border-gray-200 rounded-lg shadow-xl" data-testid="template-dropdown-list">
                {templates.length === 0 ? (
                  <div className="p-3 text-center text-gray-400 text-sm">No templates yet</div>
                ) : templatePreview ? (
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-sm">{templatePreview.name}</p>
                        <Badge variant="secondary" className="text-xs">{templatePreview.category}</Badge>
                      </div>
                      <Button size="sm" onClick={addAllFromTemplate} className="bg-blue-700 hover:bg-blue-800 gap-1 h-7 text-xs" data-testid="add-all-template">
                        <Plus className="h-3 w-3" /> Add All
                      </Button>
                    </div>
                    <ScrollArea className="max-h-48">
                      <div className="space-y-1">
                        {templatePreview.content.prescription.map((med, i) => (
                          <div key={i} className="flex items-center justify-between p-1.5 bg-gray-50 rounded text-xs">
                            <div>
                              <span className="font-medium">{med.name}</span>
                              <span className="text-gray-400 ml-1">{med.dosage} {med.frequency}</span>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => addSingleFromTemplate(med)} className="h-6 w-6 p-0" data-testid={`add-single-template-${i}`}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <Button variant="ghost" size="sm" onClick={() => setTemplatePreview(null)} className="mt-1.5 w-full text-gray-500 text-xs h-7">
                      Back
                    </Button>
                  </div>
                ) : (
                  <ScrollArea className="max-h-56">
                    {templates.map((tpl) => (
                      <button key={tpl.id} onClick={() => loadTemplate(tpl)}
                        className="w-full px-3 py-2 text-left hover:bg-blue-50 flex justify-between items-center border-b border-gray-100 last:border-0"
                        data-testid={`template-option-${tpl.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium text-sm truncate">{tpl.name}</p>
                            {tpl.is_mine && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                                Yours
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-400">{tpl.content.prescription.length} med(s)</p>
                        </div>
                        <Badge variant="secondary" className="text-xs">{tpl.category}</Badge>
                      </button>
                    ))}
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
          
          {medicines.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowSaveTemplate(true)}
              className="gap-1 h-8 text-xs px-2" data-testid="save-rx-template-btn">
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
          )}
        </div>

        {/* Inline Medicine Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input ref={topSearchRef} value={topSearchQuery} onChange={(e) => handleTopSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && topSearchResults.length > 0) addFromTopSearch(topSearchResults[0]); }}
            placeholder="Search medicine name or composition..."
            className="pl-7 h-8 text-xs" data-testid="prescription-search" />
          
          {topSearchResults.length > 0 && topSearchQuery && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto" data-testid="prescription-search-dropdown">
              {topSearchResults.map((result, i) => (
                <button key={i} type="button" onClick={() => addFromTopSearch(result)}
                  className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-gray-50 last:border-0">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs leading-tight break-words">{result.name}</p>
                      {result.from_inventory && (
                        <p className="text-[10px] text-emerald-700 font-semibold mt-0.5">In your stock</p>
                      )}
                      {result.composition && (
                        <p className="text-[10px] text-blue-600 font-medium mt-0.5 truncate" title={result.composition}>{result.composition}</p>
                      )}
                      {result.brand && <p className="text-[10px] text-gray-400 mt-0.5">{result.brand}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        result.stock > 10 ? 'bg-green-50 text-green-700' :
                        result.stock > 0 ? 'bg-amber-50 text-amber-700' :
                        'bg-red-50 text-red-700'
                      }`} data-testid={`search-stock-${i}`}>
                        <Package className="h-2.5 w-2.5" />
                        {result.stock == null ? '—'
                          : result.stock <= 0 ? 'Out of stock'
                          : `${Math.floor(result.stock)} ${result.uom || result.unit || ''}`}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <Button onClick={addMedicine} size="sm" className="gap-1 bg-blue-700 hover:bg-blue-800 h-8 text-xs" data-testid="add-medicine-btn">
          <Plus className="h-3.5 w-3.5" /> Add Medicine
        </Button>

        <VoiceDictationButton
          mode="prescription"
          inventoryHint={[
            ...topSearchResults.map((r) => r.name),
            ...quickAdds.map((q) => q.name || q.medicine_name).filter(Boolean),
          ]}
          onParsed={(payload) => setVoicePayload(payload)}
          size="sm"
          testId="voice-prescription-btn"
        />

        <span className="text-xs text-gray-400">
          {medicines.length} added
        </span>
      </div>

      {/* Quick Add Suggestions */}
      {quickAdds.length > 0 && (
        <div className="px-1 py-2 border-b border-gray-100" data-testid="quick-add-medicines">
          <div className="flex items-center gap-2 mb-1.5">
            <Zap className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
              {quickAddSource === 'symptom_map' ? 'Based on symptoms' : 'Recently used'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {quickAdds.filter(qa => !medicines.some(m => m.medicine_name === (qa.medicine_name || qa.name))).slice(0, 8).map((med, i) => (
              <button key={i}
                onClick={() => addMedicineFromSuggestion(med)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
                data-testid={`quick-add-med-${i}`}>
                <Plus className="h-3 w-3" /> {med.medicine_name || med.name}
                {med.dosage && <span className="text-amber-500 ml-0.5">{med.dosage}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Medicine List */}
      <div className="flex-1 overflow-auto">
        {medicines.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
            <p className="text-sm mb-2">No medicines added</p>
            <p className="text-xs mb-3">Click &ldquo;Add Medicine&rdquo;, load a template, or speak the prescription</p>
            <div className="flex items-center justify-center gap-2">
              <Button onClick={addMedicine} size="sm" className="bg-blue-700 hover:bg-blue-800 text-xs">
                <Plus className="h-3 w-3 mr-1" /> Add Medicine
              </Button>
              <span className="text-[10px] text-gray-400">or</span>
              <VoiceDictationButton
                mode="prescription"
                inventoryHint={[
                  ...topSearchResults.map((r) => r.name),
                  ...quickAdds.map((q) => q.name || q.medicine_name).filter(Boolean),
                ]}
                onParsed={(payload) => setVoicePayload(payload)}
                size="sm"
                label="Dictate Rx"
                testId="voice-prescription-btn-empty"
              />
            </div>
            <p className="text-[10px] text-indigo-500 mt-3 italic">
              <span className="font-semibold">Tip:</span> Say &ldquo;Pantoprazole 40mg one zero one before food for 5 days&rdquo;
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {/* Column Header */}
            <div className="grid gap-2 px-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider" style={{ gridTemplateColumns: '2.8fr 0.7fr 1fr 0.8fr 1.2fr 0.7fr 0.5fr 0.6fr' }}>
              <div>Medicine</div>
              <div>Dosage</div>
              <div>Frequency</div>
              <div>Food</div>
              <div>Timing</div>
              <div>Duration</div>
              <div>Qty</div>
              <div className="text-center">Actions</div>
            </div>

            {medicines.map((medicine, index) => {
              const rowConflict = conflictByMed.get((medicine.medicine_name || '').toLowerCase());
              return (
              <div
                key={medicine.id || index}
                className={`grid gap-2 items-start px-2 py-1.5 bg-white border rounded-lg transition-colors ${
                  rowConflict ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200 hover:border-blue-300'
                }`}
                style={{ gridTemplateColumns: '2.8fr 0.7fr 1fr 0.8fr 1.2fr 0.7fr 0.5fr 0.6fr' }}
                data-testid={`medicine-row-${index}`}
              >
                {/* Medicine Name */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                      ref={el => inputRefs.current[`${index}-name`] = el}
                      value={medicine.medicine_name || ''}
                      onChange={(e) => {
                        updateMedicine(index, 'medicine_name', e.target.value);
                        setActiveSearch(index);
                        searchMedicines(e.target.value);
                      }}
                      onFocus={() => setActiveSearch(index)}
                      onBlur={() => setTimeout(() => setActiveSearch(null), 200)}
                      placeholder="Search medicine or brand..."
                      className="pl-8 h-8 text-xs"
                      title={medicine.medicine_name}
                      data-testid={`medicine-name-${index}`}
                    />
                  </div>
                  {/* Stock + composition badge below input */}
                  {medicine.stock != null && medicine.medicine_name && !activeSearch && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5 pl-1">
                      <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0 rounded ${medicine.stock > 10 ? 'bg-green-50 text-green-700' : medicine.stock > 0 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`} data-testid={`medicine-stock-${index}`}>
                        <Package className="h-2.5 w-2.5" />
                        {medicine.stock}{medicine.unit ? ` ${medicine.unit}` : ''}
                      </span>
                      {medicine.composition && <span className="text-[10px] text-blue-600 font-medium truncate max-w-[200px]" title={medicine.composition} data-testid={`medicine-composition-${index}`}>{medicine.composition}</span>}
                      {!medicine.composition && medicine.brand && <span className="text-[10px] text-gray-400 truncate max-w-[120px]">{medicine.brand}</span>}
                      {rowConflict && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-red-100 text-red-700 border border-red-300 rounded px-1.5 py-0"
                          title={`Patient allergic to ${rowConflict.allergy}${rowConflict.reason === 'class' ? ` (${rowConflict.class}-class cross-reactivity)` : ''}`}
                          data-testid={`row-allergy-warning-${index}`}
                        >
                          <AlertTriangle className="h-2.5 w-2.5" />
                          Allergy: {rowConflict.allergy}
                        </span>
                      )}
                    </div>
                  )}
                  {rowConflict && (!medicine.stock || medicine.stock == null) && medicine.medicine_name && !activeSearch && (
                    <div className="mt-0.5 pl-1">
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-red-100 text-red-700 border border-red-300 rounded px-1.5 py-0"
                        title={`Patient allergic to ${rowConflict.allergy}${rowConflict.reason === 'class' ? ` (${rowConflict.class}-class cross-reactivity)` : ''}`}
                        data-testid={`row-allergy-warning-${index}`}
                      >
                        <AlertTriangle className="h-2.5 w-2.5" />
                        Allergy: {rowConflict.allergy}
                      </span>
                    </div>
                  )}
                  {activeSearch === index && searchResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-[460px] bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto" data-testid="medicine-search-dropdown">
                      {searchResults.map((result, i) => (
                        <button key={i} type="button" onClick={() => selectMedicine(index, result)}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-gray-50 last:border-0">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm leading-tight break-words">{result.name}</p>
                              {result.composition && (
                                <p className="text-[10px] text-blue-600 font-medium mt-0.5 truncate" title={result.composition}>{result.composition}</p>
                              )}
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                {result.brand && <span>{result.brand}</span>}
                                {result.manufacturer && result.manufacturer !== result.brand && <span> &middot; {result.manufacturer}</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                result.stock > 10 ? 'bg-green-50 text-green-700' :
                                result.stock > 0 ? 'bg-amber-50 text-amber-700' :
                                'bg-red-50 text-red-700'
                              }`}>
                                {result.stock <= 0 ? 'No stock' : `${Math.floor(result.stock)} ${result.unit || ''}`}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dosage */}
                <div>
                  <Input ref={el => inputRefs.current[`${index}-dosage`] = el}
                    value={medicine.dosage || ''} onChange={(e) => updateMedicine(index, 'dosage', e.target.value)}
                    placeholder="" className="h-8 text-xs" data-testid={`medicine-dosage-${index}`} />
                </div>

                {/* Frequency */}
                <div>
                  <Select value={medicine.frequency || ''} onValueChange={(v) => updateMedicine(index, 'frequency', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`medicine-frequency-${index}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {frequencies.map(f => <SelectItem key={f.code} value={f.code}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Food (Intake) */}
                <div>
                  <Select value={medicine.intake || ''} onValueChange={(v) => updateMedicine(index, 'intake', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`medicine-intake-${index}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {intakes.map(i => <SelectItem key={i.code} value={i.code}>{i.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Timing chips — cycle off → 1 → ½ → off */}
                <div>
                  <div className="flex gap-1">
                    {TIMING_OPTIONS.map(t => {
                      const dose = (medicine.timing_doses && medicine.timing_doses[t.code] !== undefined)
                        ? medicine.timing_doses[t.code]
                        : ((medicine.timing || []).includes(t.code) ? 1 : 0);
                      const active = dose > 0;
                      const isHalf = dose === 0.5;
                      const Icon = t.icon;
                      const tooltip = active
                        ? `${t.label}: ${isHalf ? '½ tablet' : '1 tablet'} — click to ${isHalf ? 'remove' : 'switch to ½'}`
                        : `${t.label} — click to add 1 tablet`;
                      return (
                        <button key={t.code} type="button" onClick={() => toggleTiming(index, t.code)}
                          className={`relative flex items-center justify-center w-8 h-8 rounded text-[11px] font-bold border transition-all ${
                            isHalf ? 'bg-amber-100 border-amber-400 text-amber-900'
                            : active ? 'bg-blue-100 border-blue-400 text-blue-800'
                            : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'
                          }`}
                          title={tooltip} data-testid={`timing-${t.code}-${index}`}>
                          {active ? (
                            <span className="leading-none">{isHalf ? '½' : '1'}</span>
                          ) : (
                            <Icon className="h-3.5 w-3.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <Input value={medicine.duration || ''} onChange={(e) => updateMedicine(index, 'duration', e.target.value)}
                    placeholder="5d" className="h-8 text-xs" data-testid={`medicine-duration-${index}`} />
                </div>

                {/* Quantity */}
                <div>
                  <Input value={medicine.quantity || ''} onChange={(e) => updateMedicine(index, 'quantity', e.target.value)}
                    placeholder="Qty" className="h-8 text-xs" data-testid={`medicine-quantity-${index}`} />
                </div>

                {/* Actions */}
                <div className="flex gap-0">
                  <Button variant="ghost" size="sm" onClick={() => duplicateMedicine(index)}
                    className="h-7 w-7 p-0" title="Duplicate" data-testid={`duplicate-medicine-${index}`}>
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMedicine(index)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" title="Delete" data-testid={`delete-medicine-${index}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {/* Remarks — spans full row as a secondary line so the main
                    row stays dense. Shown/hidden via a lightweight inline
                    input; printed on the prescription whenever non-empty. */}
                <div style={{ gridColumn: '1 / -1' }} className="pt-1">
                  <Input
                    value={medicine.remarks || ''}
                    onChange={(e) => updateMedicine(index, 'remarks', e.target.value)}
                    placeholder="Remarks / special instructions (optional) — e.g. Take with warm water, stop if rash appears"
                    className="h-7 text-[11px] bg-gray-50 border-gray-200 placeholder:text-gray-400"
                    data-testid={`medicine-remarks-${index}`}
                  />
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Save as Template Drawer */}
      <DrawerShell
        open={showSaveTemplate}
        onClose={() => setShowSaveTemplate(false)}
        eyebrow="Reusable Bundle"
        title="Save Prescription Template"
        subtitle={`${medicines.length} medicine${medicines.length !== 1 ? 's' : ''} will be saved`}
        accent="#1d4ed8"
        icon={<Save className="h-5 w-5" />}
        width="md"
        testId="save-template-dialog"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Cancel</Button>
            <Button onClick={async () => { await handleSaveTemplate(); await reloadTemplates(); }}
              disabled={!templateName.trim() || savingTemplate} className="bg-blue-700 hover:bg-blue-800"
              data-testid="save-template-confirm">
              {savingTemplate ? 'Saving...' : 'Save Template'}
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Template Name</Label>
            <Input id="template-name" value={templateName} onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Fever with antibiotics" data-testid="template-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="template-category">Category</Label>
            <Input id="template-category" value={templateCategory} onChange={(e) => setTemplateCategory(e.target.value)}
              placeholder="e.g., fever, diabetes, custom" data-testid="template-category-input" />
          </div>
        </div>
      </DrawerShell>

      {/* Duplicate Medicine Alert Drawer */}
      <DrawerShell
        open={!!duplicateAlert}
        onClose={() => setDuplicateAlert(null)}
        eyebrow="Already in Rx"
        title="Medicine already added"
        subtitle="Edit the existing row instead of adding a duplicate"
        accent="#d97706"
        icon={<AlertTriangle className="h-5 w-5" />}
        width="sm"
        testId="duplicate-medicine-dialog"
        footer={
          <Button onClick={() => setDuplicateAlert(null)} className="bg-blue-700 hover:bg-blue-800" data-testid="duplicate-ok-btn">
            OK
          </Button>
        }
      >
        <div className="p-5">
          <p className="text-sm text-zinc-700">
            <span className="font-semibold">{duplicateAlert}</span> is already in the prescription list.
          </p>
          <p className="text-xs text-zinc-500 mt-2">Please modify the existing entry instead of adding a duplicate.</p>
        </div>
      </DrawerShell>

      {/* Voice dictation review modal — doctor verifies the AI-parsed
          medicines before they land in the prescription list. */}
      <VoiceConfirmModal
        open={!!voicePayload}
        onClose={() => setVoicePayload(null)}
        payload={voicePayload}
        mode="prescription"
        onConfirm={handleVoiceConfirm}
      />
    </div>
  );
}
